import _ from "lodash";
import Vue from "vue";
import { doProductTypesMatch, getLivingPathway, getLivingTurnkeyType, getPropertyTypeFromProperty, isLivingEntryRoute, isLivingOptionRoute, isProductRoute, isSubsectionOrPropertyRoute, isSubsectionRoute } from "@/utils/livingRoute";

const hasRangeIntersection = (range, minSelectedValue, maxSelectedValue) => {
  if (!Array.isArray(range) || !range.length) return false;

  const numericRange = range.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (!numericRange.length) return false;

  const rangeMin = Math.min(...numericRange);
  const rangeMax = Math.max(...numericRange);

  return !(rangeMin > maxSelectedValue || rangeMax < minSelectedValue);
};

const hasIntersection = (values, selectedValues) => {
  if (!Array.isArray(values) || !Array.isArray(selectedValues)) return false;

  return values.some((value) => selectedValues.includes(value));
};

const hasActiveFilterInput = (rootGetters) => {
  if (!(rootGetters["filter/inputType"] || [false]).includes(false)) return true;
  if (rootGetters["filter/inputView"]) return true;
  if (rootGetters["filter/inputStatus"]) return true;
  if (rootGetters["filter/inputDisplayName"]) return true;
  if (!(rootGetters["filter/inputBeds"] || [false]).includes(false)) return true;
  if (!(rootGetters["filter/inputBaths"] || [false]).includes(false)) return true;
  if (!(rootGetters["filter/inputCars"] || [false]).includes(false)) return true;
  if ((rootGetters["filter/inputLotSize"] || [0, 0])[1] !== 0) return true;
  if ((rootGetters["filter/inputLotPrice"] || [0, 0])[1] !== 0) return true;
  if ((rootGetters["filter/inputDwellingSize"] || [0, 0])[1] !== 0) return true;
  if ((rootGetters["filter/inputDwellingPrice"] || [0, 0])[1] !== 0) return true;

  return false;
};

const doesEntityMatchFilters = (filter, rootGetters) => {
  if (!filter) return !hasActiveFilterInput(rootGetters);

  const selectedTypes = rootGetters["filter/inputType"] || [false];
  if (!selectedTypes.includes(false)) {
    const filterTypes = (filter.propertyTypes || []).map((item) => item.toString().toLowerCase());
    const normalizedSelectedTypes = selectedTypes.map((item) => item.toString().toLowerCase());
    if (!hasIntersection(filterTypes, normalizedSelectedTypes)) return false;
  }

  const selectedView = rootGetters["filter/inputView"];
  if (selectedView) {
    const hasViewMatch = (filter.propertyViews || []).some((view) => view.en === selectedView.toLowerCase());
    if (!hasViewMatch) return false;
  }

  const selectedStatus = rootGetters["filter/inputStatus"];
  if (selectedStatus) {
    const hasStatusMatch = (filter.propertyStatuses || []).some((status) => status.en === selectedStatus);
    if (!hasStatusMatch) return false;
  }

  const selectedDisplayName = rootGetters["filter/inputDisplayName"];
  if (selectedDisplayName) {
    const hasDisplayNameMatch = (filter.dwellingDisplayNames || []).some((displayName) => displayName.en.toLowerCase() === selectedDisplayName.toLowerCase());
    if (!hasDisplayNameMatch) return false;
  }

  const selectedBeds = rootGetters["filter/inputBeds"] || [false];
  if (!selectedBeds.includes(false)) {
    const filterBeds = (filter.dwellingBeds || []).map((item) => item.toString());
    const normalizedSelectedBeds = selectedBeds.map((item) => item.toString());
    if (!hasIntersection(filterBeds, normalizedSelectedBeds)) return false;
  }

  const selectedBaths = rootGetters["filter/inputBaths"] || [false];
  if (!selectedBaths.includes(false)) {
    const filterBaths = (filter.dwellingBaths || []).map((item) => item.toString());
    const normalizedSelectedBaths = selectedBaths.map((item) => item.toString());
    if (!hasIntersection(filterBaths, normalizedSelectedBaths)) return false;
  }

  const selectedCars = rootGetters["filter/inputCars"] || [false];
  if (!selectedCars.includes(false)) {
    const filterCars = (filter.dwellingCars || []).map((item) => item.toString());
    const normalizedSelectedCars = selectedCars.map((item) => item.toString());
    if (!hasIntersection(filterCars, normalizedSelectedCars)) return false;
  }

  const inputLotSize = rootGetters["filter/inputLotSize"];
  if (inputLotSize) {
    const [minSelectedValue, maxSelectedValue] = inputLotSize;
    if (maxSelectedValue !== 0 && !hasRangeIntersection(filter.sizeRange, minSelectedValue, maxSelectedValue)) return false;
  }

  const inputLotPrice = rootGetters["filter/inputLotPrice"];
  if (inputLotPrice) {
    const [minSelectedValue, maxSelectedValue] = inputLotPrice;
    if (maxSelectedValue !== 0 && !hasRangeIntersection(filter.lotPriceRange, minSelectedValue, maxSelectedValue)) return false;
  }

  const inputDwellingSize = rootGetters["filter/inputDwellingSize"];
  if (inputDwellingSize) {
    const [minSelectedValue, maxSelectedValue] = inputDwellingSize;
    if (maxSelectedValue !== 0 && !hasRangeIntersection(filter.dwellingSizeRange, minSelectedValue, maxSelectedValue)) return false;
  }

  const inputDwellingPrice = rootGetters["filter/inputDwellingPrice"];
  if (inputDwellingPrice) {
    const [minSelectedValue, maxSelectedValue] = inputDwellingPrice;
    if (maxSelectedValue !== 0 && !hasRangeIntersection(filter.dwellingPriceRange, minSelectedValue, maxSelectedValue)) return false;
  }

  return true;
};

const getPathwayPropertyType = (pathway) => {
  switch (getLivingPathway({ params: { pathway } })) {
    case "land":
      return "land";
    case "houses":
      return "house";
    case "townhouses":
      return "townhouse";
    case "terraces":
      return "terrace";
    case "villas":
      return "villa";
    case "apartments":
      return "apartments";
    default:
      return null;
  }
};

const propertyMatchesPathway = (property, pathway, turnkeyType = "") => {
  if (pathway === "turnkeys") {
    if (!property || !property.dwelling) return false;
    if (!turnkeyType) return true;

    return doProductTypesMatch(getPropertyTypeFromProperty(property), turnkeyType);
  }

  const propertyType = getPathwayPropertyType(pathway);
  if (!propertyType) return true;

  return getPropertyTypeFromProperty(property) === propertyType;
};

const shouldHideSoldLivingOption = (livingOption, rootGetters) => {
  return livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods;
};

const getSubsectionById = (getters, subsectionId) => {
  return _.find(getters.subsections, (subsection) => subsection._id === subsectionId);
};

const getPropertyById = (getters, propertyId) => {
  return _.find(getters.properties, (property) => property._id === propertyId);
};

const subsectionHasMatchingProperties = (subsection, getters, pathway, turnkeyType = "") => {
  if (!subsection) return false;

  const directPropertyMatch = (subsection.propertyIds || []).some((propertyId) => {
    const property = getPropertyById(getters, propertyId);
    return property && propertyMatchesPathway(property, pathway, turnkeyType);
  });

  if (directPropertyMatch) return true;

  return (subsection.subsectionIds || []).some((subsectionId) => {
    return subsectionHasMatchingProperties(getSubsectionById(getters, subsectionId), getters, pathway, turnkeyType);
  });
};

const filterSubsectionsByPathway = (subsectionIds, getters, pathway, productRoute, turnkeyType = "") => {
  return subsectionIds
    .map((subsectionId) => getSubsectionById(getters, subsectionId))
    .filter((subsection) => {
      if (!subsection) return false;
      if (!productRoute) return subsection.type === pathway;
      if (pathway === "masterplan") return true;

      return subsectionHasMatchingProperties(subsection, getters, pathway, turnkeyType);
    });
};

const livingOptionMatchesPathway = (livingOption, getters, pathway, rootGetters, turnkeyType = "") => {
  let show = (livingOption.subsectionIds || []).some((subsectionId) => {
    return subsectionHasMatchingProperties(getSubsectionById(getters, subsectionId), getters, pathway, turnkeyType);
  });

  if (shouldHideSoldLivingOption(livingOption, rootGetters)) {
    show = false;
  }

  return show;
};

export default {
  namespaced: true,
  state: {
    livingOptions: [],
    subsections: [],
    properties: [],
    dwellings: [],
    packages: [],

    activeSlideId: "",

    activeVirtualTourUrl: "",
  },

  mutations: {
    SET_LIVING_OPTIONS(state, livingOptions) {
      state.livingOptions = livingOptions;
    },

    SET_SUBSECTIONS(state, subsections) {
      state.subsections = subsections;
    },

    SET_PROPERTIES(state, properties) {
      state.properties = properties;
    },

    SET_DWELLINGS(state, dwellings) {
      state.dwellings = dwellings;
    },

    SET_TURNKEYS(state, turnkeys) {
      state.turnkeys = turnkeys;
    },

    SET_PACKAGES(state, packages) {
      state.packages = packages;
    },

    SET_ACTIVE_SLIDE_ID: (state, id) => {
      state.activeSlideId = id;
    },

    SET_ACTIVE_VIRTUAL_TOUR_URL: (state, id) => {
      state.activeVirtualTourUrl = id;
    },
  },

  getters: {
    activeSlide: (state) => {
      const slide = state.packages.find((_package) => _package._id === state.activeSlideId);

      if (slide) {
        return slide;
      } else {
        return null;
      }
    },

    activeLivingOption: (state, getters, rootState) => {
      const livingOptionId = rootState.route.params.livingOptionId;

      if (livingOptionId) {
        return _.find(getters.livingOptions, (livingOption) => {
          return livingOption._id === livingOptionId;
        });
      } else {
        return null;
      }
    },

    activeLivingOptionSubsections(state, getters, rootState) {
      const routeName = rootState.route.name;

      if (isLivingOptionRoute(routeName) || isSubsectionRoute(routeName)) {
        return _.sortBy(getters.activeSubsections, (subsection) => {
          const [marker] = subsection.markers || [];
          return marker ? marker.x : Number.MAX_SAFE_INTEGER;
        });
      }

      return [];
    },

    activeLivingOptionProperties(state, getters, rootState) {
      const routeName = rootState.route.name;

      if (isSubsectionOrPropertyRoute(routeName) || routeName === "live-display-homes" || routeName === "live-display-homes-property") {
        return _.sortBy(getters.activeProperties, (property) => {
          return property.marker ? property.marker.x + property.marker.y : Number.MAX_SAFE_INTEGER;
        });
      }

      return [];
    },

    activeLivingOptionTags(state, getters, rootState) {
      const routeName = rootState.route.name;

      if (isLivingEntryRoute(routeName)) {
        return _.sortBy(getters.activeLivingEntryOptions, (livingOption) => {
          const [marker] = livingOption.markers || [];
          return marker ? marker.x : Number.MAX_SAFE_INTEGER;
        });
      }

      return [];
    },

    activeProperty: (state, getters, rootState) => {
      if (rootState.route.params.propertyId) {
        let property = _.find(getters.properties, (property) => {
          return property._id === rootState.route.params.propertyId;
        });

        if (!property) return null;

        const associatedPackages = state.packages.filter((_package) => {
          return _package.active && _package.property.label === property.label;
        });

        property["associatedPackages"] = associatedPackages;

        return property;
      } else {
        return null;
      }
    },

    activeProperties: (state, getters, rootState, rootGetters) => {
      if (!getters.activeSubsection) return [];
      const pathway = getLivingPathway(rootState.route);
      const turnkeyType = getLivingTurnkeyType(rootState.route);
      const productRoute = isProductRoute(rootState.route);
      const properties = getters.properties.filter((property) => {
        if (!getters.activeSubsection.propertyIds.includes(property._id)) return false;
        if (!productRoute || pathway === "masterplan") return true;

        return propertyMatchesPathway(property, pathway, turnkeyType);
      });

      return properties.map((property) => {
        const productType = productRoute ? getPropertyTypeFromProperty(property) : getters.activeSubsection.type.charAt(getters.activeSubsection.type.length - 1) === "s" ? getters.activeSubsection.type.substring(0, getters.activeSubsection.type.length - 1) : getters.activeSubsection.type || "land";
        property.productType = productType;

        // filter properties
        Vue.set(property, "filtered", false);

        if (!rootGetters["filter/inputType"].includes(false)) {
          const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);

          if (dwelling) {
            const type = dwelling.type.toLowerCase();
            if (!rootGetters["filter/inputType"].includes(type)) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (rootGetters["filter/inputView"]) {
          if (rootGetters["filter/inputView"] !== property.viewLabel.en) {
            Vue.set(property, "filtered", true);
          }
        }

        if (rootGetters["filter/inputStatus"]) {
          if (rootGetters["filter/inputStatus"] !== property.status) {
            Vue.set(property, "filtered", true);
          }
        }

        if (rootGetters["filter/inputDisplayName"]) {
          const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
          if (dwelling) {
            if (dwelling.displayName.en.toLowerCase() !== rootGetters["filter/inputDisplayName"].toLowerCase()) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (!rootGetters["filter/inputBeds"].includes(false)) {
          const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
          if (dwelling) {
            const beds = parseFloat(dwelling.beds);
            if (!rootGetters["filter/inputBeds"].includes(beds)) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (!rootGetters["filter/inputBaths"].includes(false)) {
          const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
          if (dwelling) {
            const baths = parseFloat(dwelling.baths);
            if (!rootGetters["filter/inputBaths"].includes(baths)) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (!rootGetters["filter/inputCars"].includes(false)) {
          const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
          if (dwelling) {
            const cars = parseFloat(dwelling.cars);
            if (!rootGetters["filter/inputCars"].includes(cars)) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (rootGetters["filter/inputLotSize"]) {
          const minSelectedValue = rootGetters["filter/inputLotSize"][0];
          const maxSelectedValue = rootGetters["filter/inputLotSize"][1];

          if (maxSelectedValue !== 0) {
            if (minSelectedValue > property.size || maxSelectedValue < property.size) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (rootGetters["filter/inputLotPrice"]) {
          const minSelectedValue = rootGetters["filter/inputLotPrice"][0];
          const maxSelectedValue = rootGetters["filter/inputLotPrice"][1];

          if (maxSelectedValue !== 0) {
            if (minSelectedValue > property.price || maxSelectedValue < property.price) {
              Vue.set(property, "filtered", true);
            }
          }
        }

        if (rootGetters["filter/inputDwellingSize"]) {
          const minSelectedValue = rootGetters["filter/inputDwellingSize"][0];
          const maxSelectedValue = rootGetters["filter/inputDwellingSize"][1];

          if (maxSelectedValue !== 0) {
            const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
            if (dwelling) {
              if (minSelectedValue > dwelling.size || maxSelectedValue < dwelling.size) {
                Vue.set(property, "filtered", true);
              }
            }
          }
        }

        if (rootGetters["filter/inputDwellingPrice"]) {
          const minSelectedValue = rootGetters["filter/inputDwellingPrice"][0];
          const maxSelectedValue = rootGetters["filter/inputDwellingPrice"][1];

          if (maxSelectedValue !== 0) {
            const dwelling = state.dwellings.find((dwelling) => dwelling._id === property.dwellingId);
            if (dwelling) {
              if (minSelectedValue > dwelling.price || maxSelectedValue < dwelling.price) {
                Vue.set(property, "filtered", true);
              }
            }
          }
        }

        return property;
      });

      // if (!getters.activeSubsection) return []
      // return getters.activeSubsection.properties.map(property => {
      //   const productType = getters.activeSubsection.type.charAt(getters.activeSubsection.type.length - 1) === 's' ? getters.activeSubsection.type.substring(0, getters.activeSubsection.type.length - 1) : getters.activeSubsection.type
      //   property.productType = productType
      //   return property
      // })
    },

    activeDwellings: (state, getters) => {
      if (!getters.activeProperties) return [];

      return getters.activeProperties.filter((property) => property.dwelling !== null).map((property) => property.dwelling);
    },

    activeSubsection: (state, getters, rootState) => {
      if (rootState.route.params.livingOptionId && rootState.route.params.subsectionId) {
        return _.find(getters.subsections, (subsection) => {
          return subsection._id === rootState.route.params.subsectionId;
        });
      } else if (rootState.route.name === "live-display-homes" || rootState.route.name === "live-display-homes-property") {
        return _.find(getters.subsections, (subsection) => {
          return subsection.isDisplayVillage;
        });
      } else {
        return null;
      }
    },

    livingOptionSubsections: (state, getters, rootState) => {
      const activeLivingOption = getters.activeLivingOption;
      if (!activeLivingOption) return [];

      const pathway = getLivingPathway(rootState.route);
      const turnkeyType = getLivingTurnkeyType(rootState.route);
      const productRoute = isProductRoute(rootState.route);

      if (!productRoute || pathway === "masterplan") {
        return activeLivingOption.subsectionIds.map((subsectionId) => getSubsectionById(getters, subsectionId)).filter(Boolean);
      }

      return filterSubsectionsByPathway(activeLivingOption.subsectionIds, getters, pathway, productRoute, turnkeyType);
    },

    activeSubsections: (state, getters, rootState, rootGetters) => {
      const pathway = getLivingPathway(rootState.route);
      const turnkeyType = getLivingTurnkeyType(rootState.route);
      const productRoute = isProductRoute(rootState.route);
      const updateFilteredState = (subsectionIds) => {
        subsectionIds.forEach((subsectionId) => {
          const subsection = getSubsectionById(getters, subsectionId);
          if (subsection) {
            const isMatch = doesEntityMatchFilters(subsection.filter, rootGetters);
            console.log(subsection.label, isMatch);
            Vue.set(subsection, "filtered", !isMatch);
          }
        });
      };

      const activeSubsection = getters.activeSubsection;
      const activeLivingOption = getters.activeLivingOption;

      if (activeLivingOption && Array.isArray(activeLivingOption.subsectionIds)) {
        updateFilteredState(activeLivingOption.subsectionIds);
      }

      if (activeSubsection && activeSubsection.parent && activeSubsection.parent.type === "subsection") {
        const parentSubsection = getSubsectionById(getters, activeSubsection.parent.id);
        if (parentSubsection && Array.isArray(parentSubsection.subsectionIds)) {
          updateFilteredState(parentSubsection.subsectionIds);
        }
      }

      if (activeSubsection && Array.isArray(activeSubsection.subsectionIds)) {
        updateFilteredState(activeSubsection.subsectionIds);
      }

      if (activeSubsection) {
        return filterSubsectionsByPathway(activeSubsection.subsectionIds, getters, pathway, productRoute, turnkeyType);
      }

      if (activeLivingOption) {
        return getters.livingOptionSubsections;
      }

      return [];
    },

    activeVirtualTourUrl: (state) => {
      return state.activeVirtualTourUrl;
    },

    livingOptions: (state, getters, rootState, rootGetters) => {
      const livingOptions = rootState.hideLivingOptions ? state.livingOptions.filter((item) => !rootState.hideLivingOptions.split(",").includes(item._id)) : state.livingOptions;

      livingOptions.forEach((livingOption) => {
        const isMatch = doesEntityMatchFilters(livingOption.filter, rootGetters);
        Vue.set(livingOption, "filtered", !isMatch);
      });

      return livingOptions;
    },

    activeLivingEntryOptions: (state, getters, rootState, rootGetters) => {
      const pathway = getLivingPathway(rootState.route);
      const turnkeyType = getLivingTurnkeyType(rootState.route);

      if (pathway === "masterplan") {
        return getters.livingOptions.filter((livingOption) => {
          if (livingOption.availablePropertiesCount !== 0 || (rootGetters.newConfig.general && rootGetters.newConfig.general.showSoldNeighbourhoods)) {
            return livingOption;
          }
        });
      }

      return getters.livingOptions.filter((livingOption) => {
        return livingOptionMatchesPathway(livingOption, getters, pathway, rootGetters, turnkeyType);
      });
    },

    activeLivingEntryOptionsSlides: (state, getters, rootState, rootGetters) => {
      const pathway = getLivingPathway(rootState.route);
      const turnkeyType = getLivingTurnkeyType(rootState.route);

      if (pathway === "masterplan") {
        return getters.livingOptions.filter((livingOption) => {
          if (livingOption.availablePropertiesCount !== 0 || (rootGetters.newConfig.general && rootGetters.newConfig.general.showSoldSlides)) {
            return livingOption;
          }
        });
      }

      return getters.livingOptions.filter((livingOption) => {
        return livingOptionMatchesPathway(livingOption, getters, pathway, rootGetters, turnkeyType);
      });
    },

    livingOptionsApartments: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          // show if living option subsections contain land
          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "apartments") {
                show = true;
              }
            });

          // hide if sold out
          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsHouses: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          // show if living option subsections contain land
          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "houses") {
                show = true;
              }
            });

          // hide if sold out
          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsLand: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          // show if living option subsections contain land
          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "land") {
                show = true;
              }
            });

          // hide if sold out
          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          // show if there are not released properties (exist on staging only)
          if (livingOption.notReleasedPropertiesCount > 0) {
            show = true;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsTownhouses: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          // show if living option subsections contain land
          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "townhouses") {
                show = true;
              }
            });

          // hide if sold out
          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsTerraces: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "terraces") {
                show = true;
              }
            });

          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsVillas: (state, getters, rootState, rootGetters) => {
      if (getters.livingOptions.length) {
        return getters.livingOptions.filter((livingOption) => {
          let show = false;

          livingOption.subsections &&
            livingOption.subsections.forEach((subsection) => {
              if (subsection.type === "villas") {
                show = true;
              }
            });

          if (livingOption.availablePropertiesCount === 0 && rootGetters.newConfig.general && !rootGetters.newConfig.general.showSoldNeighbourhoods) {
            show = false;
          }

          return show;
        });
      } else {
        return [];
      }
    },

    livingOptionsPackages: (state) => {
      return state.packages;
    },

    packages: (state) => {
      return state.packages;
    },

    livingOptionsDwellings: (state) => {
      return state.dwellings;
    },

    land: (state) => {
      if (state.properties.length === 0) return [];
      let land = _.filter(state.properties, (property) => !property.dwelling);
      return land;
    },

    townhouses: (state) => {
      if (state.properties.length === 0) return [];
      let townhouses = _.filter(state.properties, (property) => property.dwelling && property.dwelling.type === "townhouse");
      return townhouses;
    },

    terraces: (state) => {
      if (state.properties.length === 0) return [];
      return _.filter(state.properties, (property) => property.dwelling && property.dwelling.type === "terrace");
    },

    villas: (state) => {
      if (state.properties.length === 0) return [];
      return _.filter(state.properties, (property) => property.dwelling && property.dwelling.type === "villa");
    },

    apartments: (state) => {
      if (state.properties.length === 0) return [];
      let apartments = _.filter(state.properties, (property) => property.dwelling && property.dwelling.type === "apartments");
      return apartments;
    },

    houses: (state) => {
      if (state.properties.length === 0) return [];
      let houses = _.filter(state.properties, (property) => property.dwelling && property.dwelling.type === "house");
      return houses;
    },

    properties: (state) => {
      return state.properties;
    },

    dwellings: (state) => {
      return state.dwellings;
    },

    turnkeys: (state) => {
      return state.turnkeys;
    },

    displayHomes: (state) => {
      if (state.properties.length === 0) return [];
      return _.filter(state.properties, (property) => property.isDisplayHome);
    },

    // Returns an array of Subsection objects with their associated Properties nested inside
    // Also sets a "filter" flag to true if all properties have been filtered out
    // Also handles nested subsections
    subsections: (state) => {
      return state.subsections;
    },
  },
};
